import React, { useEffect, useState } from "react";
import { ShareNetwork, ArrowUpRight, Lightning } from "@phosphor-icons/react";
import { logError } from "@/lib/logger";
import api from "@/lib/api";

/**
 * ShareAnalyticsPanel — recruiter-facing view of the share funnel.
 *
 * Renders three sections from GET /api/share/analytics/summary:
 *   1. Headline KPIs — taps, lands, applies, land-rate, apply-rate.
 *   2. By channel — which social network drives real applications.
 *   3. Custom headline A/B lift — does adding a hook line convert better?
 *
 * Deliberately shipped as a card block inside <Analytics />, not a
 * separate page — recruiters already come to /analytics for pipeline
 * data, so co-locating the share funnel puts insights where decisions
 * are made ("should I keep spending time on Facebook shares?").
 *
 * Empty-state: shows a friendly nudge to open ShareJobModal on any JD.
 */
export default function ShareAnalyticsPanel() {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(14);

    useEffect(() => {
        api.get(`/share/analytics/summary?days=${days}`)
            .then((r) => setData(r.data))
            .catch((e) => logError("share analytics load", e));
    }, [days]);

    if (!data) {
        return (
            <div className="mt-10 bg-white border border-[#E5E5E5] p-6" data-testid="share-analytics-loading">
                <p className="text-[#525252]">Loading share analytics…</p>
            </div>
        );
    }

    const pct = (n) => `${Math.round((n || 0) * 100)}%`;
    const t = data.totals || {};
    const noData = !t.taps && !t.lands && !t.applies;

    return (
        <div className="mt-10" data-testid="share-analytics-panel">
            <div className="flex items-end justify-between mb-4 sm:mb-6 flex-wrap gap-3">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 flex items-center gap-2">
                        <ShareNetwork size={12} weight="bold" /> Share Funnel · Last {days} days
                    </p>
                    <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-ink">
                        Where your shares actually convert.
                    </h2>
                </div>
                <div className="flex gap-1 border border-[#E5E5E5] p-0.5">
                    {[7, 14, 30, 90].map((d) => (
                        <button
                            key={d}
                            type="button"
                            data-testid={`share-analytics-range-${d}`}
                            onClick={() => setDays(d)}
                            className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                                days === d ? "bg-ink text-white" : "text-[#525252] hover:bg-[#F5F5F5]"
                            }`}
                        >
                            {d}d
                        </button>
                    ))}
                </div>
            </div>

            {noData ? (
                <div className="bg-white border border-dashed border-[#E5E5E5] p-8 text-center" data-testid="share-analytics-empty">
                    <ShareNetwork size={28} weight="bold" className="mx-auto text-[#A3A3A3] mb-3" />
                    <p className="font-bold text-ink mb-1">No shares tracked yet</p>
                    <p className="text-sm text-[#525252]">
                        Open any Job Description and hit the Share button — every tap, landing, and application will appear here.
                    </p>
                </div>
            ) : (
                <>
                    {/* KPI row */}
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 mb-6">
                        <Kpi label="Shares" value={t.taps || 0} />
                        <Kpi label="Landings" value={t.lands || 0} />
                        <Kpi label="Applies" value={t.applies || 0} accent="text-emerald-600" />
                        <Kpi label="Click-through" value={pct(t.taps ? (t.lands / t.taps) : 0)} />
                        <Kpi label="Share→Apply" value={pct(t.taps ? (t.applies / t.taps) : 0)} accent="text-emerald-600" />
                    </div>

                    <div className="grid lg:grid-cols-2 gap-4 mb-6">
                        {/* By channel */}
                        <div className="bg-white border border-[#E5E5E5]" data-testid="share-by-channel">
                            <div className="px-5 py-4 border-b border-[#E5E5E5]">
                                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">By channel</p>
                                <h3 className="font-heading font-black text-xl tracking-tight">Which platform pulls its weight</h3>
                            </div>
                            {data.by_channel.length === 0 ? (
                                <p className="p-6 text-sm text-[#525252]">No channel data yet.</p>
                            ) : (
                                <div>
                                    {data.by_channel.map((c) => (
                                        <div key={c.channel} className="flex items-center justify-between px-5 py-3 border-b border-[#F5F5F5] last:border-b-0"
                                             data-testid={`share-channel-${c.channel}`}>
                                            <div className="flex items-center gap-3 min-w-0">
                                                <span className="capitalize font-bold text-ink">{c.channel.replace("_", " ")}</span>
                                            </div>
                                            <div className="flex items-center gap-4 text-xs">
                                                <span className="text-[#525252]"><b className="text-ink">{c.taps}</b> shares</span>
                                                <span className="text-[#525252]"><b className="text-ink">{c.lands}</b> lands</span>
                                                <span className="text-emerald-700"><b>{c.applies}</b> applies</span>
                                                <span className={`ml-1 px-2 py-0.5 text-[10px] font-black tracking-wider ${
                                                    c.apply_rate > 0.05 ? "bg-emerald-100 text-emerald-800" : "bg-[#F5F5F5] text-[#525252]"
                                                }`}>
                                                    {pct(c.apply_rate)}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* By headline A/B */}
                        <div className="bg-white border border-[#E5E5E5]" data-testid="share-by-headline">
                            <div className="px-5 py-4 border-b border-[#E5E5E5]">
                                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] flex items-center gap-1.5">
                                    <Lightning size={11} weight="bold" /> Custom headline A/B
                                </p>
                                <h3 className="font-heading font-black text-xl tracking-tight">
                                    Does adding a hook line convert more?
                                </h3>
                            </div>
                            {data.by_headline.length === 0 ? (
                                <p className="p-6 text-sm text-[#525252]">Not enough shares to compare yet.</p>
                            ) : (
                                <div>
                                    {data.by_headline.map((h) => (
                                        <div key={h.variant} className="px-5 py-3 border-b border-[#F5F5F5] last:border-b-0"
                                             data-testid={`share-headline-${h.variant}`}>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="font-bold capitalize text-ink">
                                                    {h.variant === "custom" ? "With custom headline" : "Default caption"}
                                                </span>
                                                <span className={`px-2 py-0.5 text-[10px] font-black tracking-wider ${
                                                    h.apply_rate > 0.05 ? "bg-emerald-100 text-emerald-800" : "bg-[#F5F5F5] text-[#525252]"
                                                }`}>
                                                    {pct(h.apply_rate)}
                                                </span>
                                            </div>
                                            <div className="flex gap-4 text-xs text-[#525252]">
                                                <span><b className="text-ink">{h.taps}</b> shares</span>
                                                <span><b className="text-ink">{h.lands}</b> lands</span>
                                                <span className="text-emerald-700"><b>{h.applies}</b> applies</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Top jobs */}
                    {data.top_jobs && data.top_jobs.length > 0 && (
                        <div className="bg-white border border-[#E5E5E5]" data-testid="share-top-jobs">
                            <div className="px-5 py-4 border-b border-[#E5E5E5]">
                                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">Top shared roles</p>
                                <h3 className="font-heading font-black text-xl tracking-tight">Where your reach is going</h3>
                            </div>
                            {data.top_jobs.slice(0, 5).map((j, i) => (
                                <a key={j.job_id} href={`/jobs/${j.job_id}`}
                                   data-testid={`share-top-job-${i}`}
                                   className="flex items-center justify-between px-5 py-4 border-b border-[#F5F5F5] last:border-b-0 hover:bg-[#FAFAFA] group">
                                    <div className="flex items-center gap-4 min-w-0">
                                        <span className="font-heading font-black text-xl tracking-tighter text-[#A3A3A3] w-8">
                                            {(i + 1).toString().padStart(2, "0")}
                                        </span>
                                        <p className="font-bold text-ink truncate">{j.title}</p>
                                    </div>
                                    <div className="flex items-center gap-3 text-xs shrink-0">
                                        <span className="text-[#525252]"><b className="text-ink">{j.taps}</b> shares</span>
                                        <span className="text-emerald-700"><b>{j.applies}</b> applies</span>
                                        <ArrowUpRight size={14} weight="bold" className="text-[#A3A3A3] group-hover:text-ink" />
                                    </div>
                                </a>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function Kpi({ label, value, accent }) {
    return (
        <div className="bg-white border border-[#E5E5E5] p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">{label}</p>
            <p className={`font-heading font-black text-3xl sm:text-4xl tracking-tighter ${accent || "text-ink"}`}>
                {value}
            </p>
        </div>
    );
}
